/**
 * Serviço de API para gestão de tags
 * Comunicação com endpoints do backend FastAPI
 */

import api from './api.ts';

// ============= TIPOS E INTERFACES =============

export interface TagResponse {
  id: number;
  clinic_id: number;
  name: string;
  category: string;
  color: string;
  description?: string;
  usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TagCreate {
  name: string;
  category?: string;
  color?: string;
  description?: string;
}

export interface TagUpdate {
  name?: string;
  category?: string;
  color?: string;
  description?: string;
  is_active?: boolean;
}

export interface ContactTagRequest {
  tag_ids: number[];
}

export interface TagSearchResponse {
  tags: TagResponse[];
  total: number;
  suggestions: string[];
  categories: string[];
}

export interface TagAnalytics {
  total_tags: number;
  total_usage: number;
  active_tags: number;
  inactive_tags: number;
  top_tags: Array<{
    tag_name: string;
    usage_count: number;
    category: string;
    color: string;
  }>;
  categories_usage: Array<{
    category: string;
    count: number;
    color: string;
  }>;
  recent_activity: Array<{
    tag_name: string;
    action: string;
    timestamp: string;
    contact_name?: string;
  }>;
  usage_by_month: Array<{
    month: string;
    usage_count: number;
    created_count: number;
  }>;
}

export interface ContactTagsSummary {
  contact_id: number;
  tags: TagResponse[];
  total_tags: number;
  categories: string[];
  last_updated: string;
}

export interface LeadTagsSummary {
  lead_id: number;
  tags: TagResponse[];          // Tags manuais do lead
  inherited_tags: TagResponse[]; // Tags herdadas do contato
  manual_tags: TagResponse[];    // Tags manuais do lead
  total_tags: number;
  categories: string[];
  last_updated: string;
}

export interface BatchOperationResponse {
  success_count: number;
  error_count: number;
  total_processed: number;
  errors: string[];
  details: Record<string, any>;
}

export interface LeadTagsBatchResponse {
  results: Record<number, LeadTagsSummary>;
  failed_ids: number[];
}

export interface CategoryResponse {
  id: number;
  clinic_id: number;
  name: string;
  description?: string;
  color: string;
  tag_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CategoryCreate {
  name: string;
  description?: string;
  color?: string;
}

export interface CategoryUpdate {
  name?: string;
  description?: string;
  color?: string;
}

// ============= SERVIÇO DE TAGS =============

export class TagsService {
  static async getTags(clinicId: number, params?: {
    category?: string;
    search?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<TagResponse[]> {
    try {
      const requestedLimit = Math.min(Math.max(params?.limit ?? 20, 1), 1000);
      const requestedOffset = Math.max(params?.offset ?? 0, 0);
      const pageSize = 100;
      const startPage = Math.floor(requestedOffset / pageSize) + 1;
      const pageOffset = requestedOffset % pageSize;
      const tags: TagResponse[] = [];
      let page = startPage;
      let skip = pageOffset;

      while (tags.length < requestedLimit) {
        const response = await api.get(`/api/tags/management`, {
          params: {
            clinic_id: clinicId,
            category: params?.category,
            search: params?.search,
            is_active: params?.is_active,
            page,
            per_page: pageSize,
          }
        });
        const pageTags: TagResponse[] = response.data.tags || response.data || [];
        const availableTags = pageTags.slice(skip);
        tags.push(...availableTags.slice(0, requestedLimit - tags.length));
        if (pageTags.length < pageSize) break;
        page += 1;
        skip = 0;
      }

      return tags;
    } catch (error: any) {
      console.error('❌ Erro ao buscar tags:', error.response?.data || error.message);
      throw error;
    }
  }

  static async createTag(clinicId: number, tagData: TagCreate): Promise<TagResponse> {
    try {
      const response = await api.post(`/api/tags/management`, {
        ...tagData,
        clinic_id: clinicId
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao criar tag:', error.response?.data || error.message);
      throw error;
    }
  }

  static async updateTag(tagId: number, tagData: TagUpdate): Promise<TagResponse> {
    try {
      const response = await api.put(`/api/tags/management/${tagId}`, tagData);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao atualizar tag:', error.response?.data || error.message);
      throw error;
    }
  }

  static async deleteTag(tagId: number): Promise<void> {
    try {
      await api.delete(`/api/tags/management/${tagId}`);
    } catch (error: any) {
      console.error('❌ Erro ao remover tag:', error.response?.data || error.message);
      throw error;
    }
  }

  static async searchTags(clinicId: number, params: {
    q?: string;
    category?: string;
    limit?: number;
  }): Promise<TagSearchResponse> {
    try {
      const response = await api.get(`/api/tags/search`, {
        params: { clinic_id: clinicId, ...params }
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao buscar tags:', error.response?.data || error.message);
      throw error;
    }
  }

  static async getTagAnalytics(clinicId: number): Promise<TagAnalytics> {
    try {
      const response = await api.get(`/api/tags/analytics`, {
        params: { clinic_id: clinicId }
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao buscar analytics:', error.response?.data || error.message);
      throw error;
    }
  }

  static async getContactTags(contactId: number): Promise<ContactTagsSummary> {
    try {
      const response = await api.get(`/api/tags/contacts/${contactId}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao buscar tags do contato:', error.response?.data || error.message);
      throw error;
    }
  }


  static async getLeadsTagsBatch(leadIds: number[]): Promise<LeadTagsBatchResponse> {
    try {
      const response = await api.post(`/api/tags/batch/leads/fetch`, {
        lead_ids: leadIds
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao buscar tags de leads em lote:', error.response?.data || error.message);
      throw error;
    }
  }

  static async getLeadTags(leadId: number): Promise<LeadTagsSummary> {
    try {
      const response = await api.get(`/api/tags/leads/${leadId}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao buscar tags do lead:', error.response?.data || error.message);
      throw error;
    }
  }

  // Atualizar tags do lead (substituir todas)
  static async updateLeadTags(leadId: number, tagIds: number[]): Promise<LeadTagsSummary> {
    try {
      const response = await api.put(`/api/tags/leads/${leadId}`, tagIds);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao atualizar tags do lead:', error.response?.data || error.message);
      throw error;
    }
  };

  // Adicionar tags ao lead (mantendo existentes)
  static async addTagsToLead(leadId: number, tagIds: number[]): Promise<TagResponse[]> {
    try {
      const response = await api.post(`/api/tags/leads/${leadId}`, { tag_ids: tagIds });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao adicionar tags ao lead:', error.response?.data || error.message);
      throw error;
    }
  };

  // Remover tag específica do lead
  static async removeTagFromLead(leadId: number, tagId: number): Promise<void> {
    try {
      await api.delete(`/api/tags/leads/${leadId}/${tagId}`);
    } catch (error: any) {
      console.error('❌ Erro ao remover tag do lead:', error.response?.data || error.message);
      throw error;
    }
  };

  static async addTagsToContact(contactId: number, tagIds: number[]): Promise<void> {
    try {
      await api.post(`/api/tags/contacts/${contactId}`, {
        tag_ids: tagIds
      });
    } catch (error: any) {
      console.error('❌ Erro ao associar tags:', error.response?.data || error.message);
      throw error;
    }
  }

  static async removeTagFromContact(contactId: number, tagId: number): Promise<void> {
    try {
      await api.delete(`/api/tags/contacts/${contactId}/${tagId}`);
    } catch (error: any) {
      console.error('❌ Erro ao remover tag:', error.response?.data || error.message);
      throw error;
    }
  }

  static async updateContactTags(contactId: number, tagIds: number[]): Promise<void> {
    // VALIDAÇÃO DO PARÂMETRO
    console.log('🔍 DEBUG TagsService.updateContactTags - contactId:', contactId, 'tipo:', typeof contactId);

    if (!contactId || contactId <= 0 || typeof contactId !== 'number') {
      const error = new Error(`ID do contato inválido: ${contactId} (tipo: ${typeof contactId})`);
      console.error('❌ TagsService.updateContactTags:', error.message);
      throw error;
    }

    try {
      const url = `/api/tags/contacts/${contactId}`;
      console.log('🌐 Chamando API:', url);

      await api.put(url, tagIds);
      console.log('✅ Tags atualizadas com sucesso');
    } catch (error: any) {
      console.error('❌ Erro na requisição:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }

  static async batchOperationContacts(params: {
    contact_ids: number[];
    tag_ids: number[];
    operation: 'add' | 'remove' | 'replace';
  }): Promise<BatchOperationResponse> {
    try {
      const response = await api.post(`/api/tags/batch/contacts`, params);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro na operação em lote:', error.response?.data || error.message);
      throw error;
    }
  }
}

// ============= SERVIÇO DE CATEGORIAS =============

export const CategoriesService = {
  async createCategory(clinicId: number, categoryData: CategoryCreate): Promise<CategoryResponse> {
    try {
      const response = await api.post(`/api/tags/categories`, {
        ...categoryData,
        clinic_id: clinicId
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao criar categoria:', error.response?.data || error.message);
      throw error;
    }
  },

  async updateCategory(categoryId: number, categoryData: CategoryUpdate): Promise<CategoryResponse> {
    try {
      const response = await api.put(`/api/tags/categories/${categoryId}`, categoryData);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao atualizar categoria:', error.response?.data || error.message);
      throw error;
    }
  },

  async deleteCategory(categoryId: number): Promise<void> {
    try {
      await api.delete(`/api/tags/categories/${categoryId}`);
    } catch (error: any) {
      console.error('❌ Erro ao remover categoria:', error.response?.data || error.message);
      throw error;
    }
  },

  async getCategoriesWithCount(clinicId: number): Promise<CategoryResponse[]> {
    try {
      const response = await api.get(`/api/tags/categories`, {
        params: { clinic_id: clinicId }
      });
      return response.data.categories || response.data || [];
    } catch (error: any) {
      console.error('❌ Erro ao buscar categorias:', error.response?.data || error.message);
      throw error;
    }
  },

  generateRandomCategoryColor(): string {
    const colors = [
      '#25D366', '#128C7E', '#DCF8C6', '#34B7F1', '#DC4743',
      '#FFA726', '#AB47BC', '#26A69A', '#FF7043', '#42A5F5',
      '#66BB6A', '#EC407A', '#5C6BC0'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  },

  isValidHexColor(color: string): boolean {
    const hexRegex = /^#[0-9A-Fa-f]{6}$/;
    return hexRegex.test(color);
  }
};

// ============= UTILITÁRIOS =============

export const TagUtils = {
  generateRandomColor(): string {
    const colors = [
      '#128C7E', '#075E54', '#DCF8C6', '#34B7F1', '#E8F5E9',
      '#F3E5F5', '#FFF3E0', '#E1F5FE', '#FFEBEE', '#F1F8E9'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  },

  isValidHexColor(color: string): boolean {
    const hexRegex = /^#[0-9A-Fa-f]{6}$/;
    return hexRegex.test(color);
  },

  normalizeTagName(name: string): string {
    return name.trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-_]/g, '')
      .replace(/\s+/g, ' ');
  },

  suggestTags(partialName: string, existingTags: TagResponse[], limit: number = 5): TagResponse[] {
    if (!partialName) return [];

    const partialLower = partialName.toLowerCase();
    return existingTags
      .filter(tag => tag.name.toLowerCase().includes(partialLower))
      .slice(0, limit);
  },

  groupTagsByCategory(tags: TagResponse[]): Record<string, TagResponse[]> {
    return tags.reduce((groups, tag) => {
      const category = tag.category || 'general';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(tag);
      return groups;
    }, {} as Record<string, TagResponse[]>);
  },

  sortTagsByUsage(tags: TagResponse[]): TagResponse[] {
    return [...tags].sort((a, b) => b.usage_count - a.usage_count);
  },

  sortTagsByName(tags: TagResponse[]): TagResponse[] {
    return [...tags].sort((a, b) => a.name.localeCompare(b.name));
  },

  getContrastColor(hexColor: string): string {
    // Remove o # se presente
    const color = hexColor.replace('#', '');

    // Converte para RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);

    // Calcula luminosidade (fórmula W3C)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Retorna cor de contraste baseada na luminosidade
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }
};

export default TagsService;
